/**
 * Neue Betreuung aus ScanSnap-Beschluss (22.10.2026) -- reine Anzeige-/Formularlogik, keine Fachentscheidung.
 * Das Backend (/api/neue-betreuung) prueft alles erneut (Bestaetigung, Dublettenpruefung, Art, Datumsformat, Dubletten-Sperre).
 */

export const ART_TEXT = { betreuerwechsel: 'Betreuerwechsel (bestehende Betreuung)', erstbestellung: 'Erstmalige Bestellung', unklar: 'Art nicht eindeutig erkannt' };

export const PERSON_FELDER = [
  ['vorname', 'Vorname'], ['nachname', 'Nachname'], ['geburtsname', 'Geburtsname'], ['geburtsdatum', 'Geburtsdatum', 'date'],
  ['strasse', 'Straße'], ['hausnummer', 'Hausnummer'], ['plz', 'PLZ'], ['ort', 'Ort'],
];
export const GERICHT_FELDER = [
  ['aktenzeichen', 'Aktenzeichen'], ['amtsgericht', 'Amtsgericht'], ['beschluss_datum', 'Beschlussdatum', 'date'], ['wirksam_ab', 'Wirksam ab (aktuelle Bestellung)', 'date'],
  ['ueberpruefung_bis', 'Überprüfung spätestens', 'date'],
];
export const FELDER = [...PERSON_FELDER, ...GERICHT_FELDER];

/** Neuanlage-Ansicht (24.10.2026): vier getrennte Abschnitte statt eines gemischten Pruefblocks. */
export const ABSCHNITTE = ['Person und Dubletten', 'Betreuerwechsel und Gerichtsdaten', 'Aufgabenbereiche und Einwilligungsvorbehalt', 'Original, Zusammenfassung und Bestätigung'];

/** 29.10.2026: Betreuerwechsel ohne historischen Beginn -- Feld bleibt leer ("noch zu klaeren"), die Neuanlage ist trotzdem moeglich. */
export const BEGINN_UNBEKANNT_HINWEIS = 'Ursprünglicher Beginn der Betreuung unbekannt. Bitte ursprünglichen Bestellungsbeschluss prüfen oder Datum beim Betreuungsgericht erfragen.';

export const EVB_STATUS_TEXT = { fortbestehend: 'besteht fort (Umfang laut Vorbeschluss)', angeordnet: 'angeordnet', nicht_angeordnet: 'nicht angeordnet' };
export const WECHSEL_FELDER = [['bisheriger_betreuer', 'Bisherige/r Betreuer/in'], ['betreuung_ursprung_beginn', 'Beginn der Betreuung insgesamt (VBVG)', 'date']];

export const KREISE = [
  ['vermoegenssorge', 'Vermögenssorge'], ['gesundheitssorge', 'Gesundheitssorge'], ['aufenthaltsbestimmung', 'Aufenthaltsbestimmung'],
  ['wohnungsangelegenheiten', 'Wohnungsangelegenheiten'], ['behoerdenangelegenheiten', 'Behörden/Sozialleistungsträger'],
  ['post_fernmeldeverkehr', 'Post/Fernmeldeverkehr'], ['vertretung_vor_gericht', 'Vertretung vor Gerichten'],
];

export const FEHLT_TEXT = {
  vorname: 'Vorname', nachname: 'Nachname', geburtsdatum: 'Geburtsdatum', aktenzeichen: 'Aktenzeichen', amtsgericht: 'Amtsgericht', beschluss_datum: 'Beschlussdatum',
  aufgabenkreise: 'Aufgabenkreise', aufgabenkreise_wortlaut: 'Aufgabenbereiche (Wortlaut)', strasse: 'Straße', plz: 'PLZ', ort: 'Ort', ueberpruefung_bis: 'Überprüfungsfrist', einwilligungsvorbehalt: 'Einwilligungsvorbehalt',
  bisheriger_betreuer: 'bisherige/r Betreuer/in', wirksam_ab: 'Wirksamkeitsdatum', betreuung_ursprung_beginn: 'Beginn der Betreuung insgesamt (VBVG)',
};

const KREIS_LABEL = Object.fromEntries([
  ['vermoegenssorge', 'Vermögenssorge'], ['gesundheitssorge', 'Gesundheitssorge'], ['aufenthaltsbestimmung', 'Aufenthaltsbestimmung'],
  ['wohnungsangelegenheiten', 'Wohnungsangelegenheiten'], ['behoerdenangelegenheiten', 'Behörden/Sozialleistungsträger'],
  ['post_fernmeldeverkehr', 'Post/Fernmeldeverkehr'], ['vertretung_vor_gericht', 'Vertretung vor Gerichten'],
]);

/** Namensreihenfolge des Kopfblocks als Hinweistext (null, wenn kein Kopfblock erkannt). */
export function reihenfolgeText(vorschlag) {
  const r = ((vorschlag && vorschlag.felder) || {}).namensreihenfolge;
  if (!r) return null;
  const form = r.wert === 'vorname_zuerst' ? 'Vorname vor Nachname' : 'Nachname vor Vorname';
  return r.gegengeprueft ? `Kopfblock nennt ${form} – am Tenor gegengeprüft.` : `Kopfblock vermutlich ${form} – nicht gegengeprüft, bitte Vor- und Nachname bestätigen.`;
}

function anzeigeWert(k, w) {
  if (k === 'aufgabenkreise') return (w || []).map((x) => KREIS_LABEL[x] || x).join(', ');
  if (k === 'aufgabenkreise_wortlaut') return (w || []).filter((x) => String(x).trim()).join(' · ');
  if (k === 'einwilligungsvorbehalt') return w === true ? 'ja' : (w === false ? 'nein' : '');
  return w || '';
}

/** Zusammenfassung fuer Abschnitt 4: jede Angabe mit Quelle (aus Beschluss / manuell / offen). Offene Angaben bleiben ausdruecklich offen. */
export function zusammenfassung(vorschlag, werte, art) {
  const felder = [...FELDER, ...(art === 'betreuerwechsel' ? WECHSEL_FELDER : []), ['aufgabenkreise_wortlaut', 'Aufgabenbereiche (Wortlaut laut Beschluss)'],
    ['aufgabenkreise', 'Interne Kategorien (zusätzlich)'], ['einwilligungsvorbehalt', 'Einwilligungsvorbehalt']];
  return felder.map(([k, label]) => {
    const w = werte[k];
    return { k, label, wert: anzeigeWert(k, w), quelle: quelle(vorschlag, k, w) };
  });
}

export const STAERKE_TEXT = { sicher: 'sichere Dublette', wahrscheinlich: 'wahrscheinliche Dublette', moeglich: 'mögliche Dublette' };
export const GRUND_TEXT = { aktenzeichen: 'gleiches Aktenzeichen', aktenzeichen_bis_auf_zusatz: 'Aktenzeichen gleich bis auf den Klammerzusatz – bitte prüfen', vorname_nachname: 'gleicher Name', nachname: 'gleicher Nachname/Geburtsname', geburtsdatum: 'gleiches Geburtsdatum', vorname: 'gleicher Vorname' };

export const grundAnzeige = (g) => (g.startsWith('resolver:') ? 'Zuordnungslogik findet diesen Klienten' : (GRUND_TEXT[g] || g));

/** Startwerte des Formulars: nur belegte Werte, alles andere leer. */
export function startWerte(vorschlag) {
  const f = (vorschlag && vorschlag.felder) || {};
  const w = {};
  [...FELDER, ...WECHSEL_FELDER].forEach(([k]) => { w[k] = (f[k] && f[k].wert) || ''; });
  w.aufgabenkreise = (f.aufgabenkreise && f.aufgabenkreise.wert) || [];
  // Gerichtswortlaut (29.10.2026): FUEHREND, je Zeile einzeln vorausgefuellt und bearbeitbar
  w.aufgabenkreise_wortlaut = [...((f.aufgabenkreise_wortlaut && f.aufgabenkreise_wortlaut.wert) || [])];
  w.einwilligungsvorbehalt = f.einwilligungsvorbehalt ? f.einwilligungsvorbehalt.wert : null;
  return w;
}

/** Wortlautzeilen wie das Backend: Rand-Leerzeichen weg, leere Zeilen weg, Dubletten weg -- sonst unveraendert. */
export const wortlautZeilen = (zeilen) => [...new Set((zeilen || []).map((z) => String(z).trim()).filter(Boolean))];

/** 'dokument' nur bei unveraendertem belegtem Wert -- sonst 'benutzer' (gleiche Regel wie im Backend). */
export function quelle(vorschlag, k, wert) {
  const f = ((vorschlag && vorschlag.felder) || {})[k];
  if (k === 'aufgabenkreise_wortlaut') wert = wortlautZeilen(wert);
  if (wert === '' || wert === null || wert === undefined || (Array.isArray(wert) && wert.length === 0)) return 'leer';
  if (!f) return 'benutzer';
  return JSON.stringify(f.wert) === JSON.stringify(wert) ? 'dokument' : 'benutzer';
}

export const starkeDubletten = (vorschlag) => ((vorschlag && vorschlag.dubletten) || []).filter((d) => d.staerke === 'sicher' || d.staerke === 'wahrscheinlich');

/** Ist das Formular absendbar? Liefert den ersten fehlenden Schritt als Text oder null. */
export function fehlenderSchritt({ vorschlag, werte, art, bestehend, dublettenGeprueft, trotzDublette, bestaetigt, konflikt, ersetzen }) {
  if (!dublettenGeprueft) return 'Bitte bestätigen, dass die Dublettenliste geprüft wurde.';
  if (!bestaetigt) return bestehend ? 'Bitte die Zuordnung ausdrücklich bestätigen.' : 'Bitte die Neuanlage ausdrücklich bestätigen.';
  if (konflikt && !ersetzen) return 'Widersprüchliche Zuordnung: bitte bestätigen, dass die bestehende Zuordnung ersetzt wird.';
  if (bestehend) return null;
  if (art !== 'betreuerwechsel' && art !== 'erstbestellung') return 'Bitte wählen: Betreuerwechsel oder erstmalige Bestellung.';
  if (!String(werte.vorname || '').trim() || !String(werte.nachname || '').trim()) return 'Vor- und Nachname sind erforderlich.';
  if (starkeDubletten(vorschlag).length > 0 && !trotzDublette) return 'Mögliche Dublette: bestehenden Klienten wählen oder „trotzdem neu anlegen“ bestätigen.';
  return null;
}

/** Request-Body fuer /bestaetigen. Alle Felder werden gesendet -- auch geleerte ('' / [] / null), damit das Backend nicht auf den
 *  erkannten Wert zurueckfaellt, wenn der Benutzer ihn bewusst entfernt hat (das Feld gilt dann als fehlend). */
export function baueBestaetigung({ werte, art, bestehend, dublettenGeprueft, trotzDublette, bestaetigt, konflikt, ersetzen }) {
  const ers = konflikt ? { zuordnung_ersetzen: !!ersetzen } : {};          // nur bei widerspruechlicher Zuordnung, nie stillschweigend
  if (bestehend) return { bestaetigt: !!bestaetigt, dubletten_geprueft: !!dublettenGeprueft, bestehender_klient_id: bestehend.klient_id, ...ers };
  const felder = {};
  [...FELDER, ...WECHSEL_FELDER].forEach(([k]) => {
    if (art !== 'betreuerwechsel' && WECHSEL_FELDER.some(([w]) => w === k)) return;
    felder[k] = String(werte[k] || '').trim();
  });
  felder.aufgabenkreise = werte.aufgabenkreise || [];
  felder.aufgabenkreise_wortlaut = wortlautZeilen(werte.aufgabenkreise_wortlaut);          // bestaetigter Gerichtswortlaut (fuehrend)
  felder.einwilligungsvorbehalt = typeof werte.einwilligungsvorbehalt === 'boolean' ? werte.einwilligungsvorbehalt : null;
  return { bestaetigt: !!bestaetigt, dubletten_geprueft: !!dublettenGeprueft, art, felder, trotz_dublette: !!trotzDublette, ...ers };
}

/** Badge-Text: Bei sicherer Dublette ist es vermutlich ein vorhandener Klient -- dann Zuordnung statt Neuanlage hervorheben. */
export function nbBadgeText(vorschlag) {
  return ((vorschlag && vorschlag.dubletten) || []).some((d) => d.staerke === 'sicher') ? 'Beschluss: vorhandener Klient? – bitte bestätigen' : 'Neue Betreuung zur Bestätigung';
}

/** Dokumente mit vorbereitetem Vorschlag zuerst, sonst Reihenfolge unveraendert (stabil). */
export function nbZuerst(liste, vorschlagZu) {
  return [...liste].map((d, i) => [d, i]).sort(([a, i], [b, j]) => (vorschlagZu[b.dokument_id] ? 1 : 0) - (vorschlagZu[a.dokument_id] ? 1 : 0) || i - j).map(([d]) => d);
}

/** Qwen-Beschlussauswertung laeuft im Hintergrund (25.10.2026): fragt nach, bis der Vorschlag vorliegt -- jede Nachfrage ist billig
 *  (Ergebnis-Cache im Backend, kein weiterer Modellaufruf). `abgebrochen()` beendet das Nachfragen (z. B. Ansicht geschlossen). */
export async function holeVorschlag(laden, dokumentId, { onLaeuft, intervallMs = 4000, maxVersuche = 90, warte = (ms) => new Promise((r) => setTimeout(r, ms)), abgebrochen = () => false } = {}) {
  for (let i = 0; i < maxVersuche; i += 1) {
    const v = await laden(dokumentId);
    if (!v || !v.in_bearbeitung) return v;
    if (onLaeuft) onLaeuft(v);
    await warte(intervallMs);
    if (abgebrochen()) return null;
  }
  throw new Error('Die Qwen-Auswertung dauert ungewöhnlich lange – bitte später erneut öffnen.');
}

export const KI_STATUS_TEXT = {
  ok: 'Qwen-Auswertung, gegen die Textprüfung abgeglichen', nicht_verfuegbar: 'Qwen nicht verfügbar – nur Textprüfung',
  antwort_ungueltig: 'Qwen-Antwort nicht auswertbar – nur Textprüfung', text_zu_lang: 'Text zu lang für Qwen – nur Textprüfung',
  unvollstaendig: 'Qwen-Auswertung unvollständig (nicht alle Textabschnitte auswertbar) – nur Textprüfung',
};
export const kiStatusText = (v) => (v && v.ki ? (KI_STATUS_TEXT[v.ki.status] || v.ki.status) : null);

/** Nachweis der vollstaendigen Erstanalyse (28.10.2026): wie viel Text in wie vielen Abschnitten an Qwen ging. */
export function abdeckungText(v) {
  const a = v && v.ki && v.ki.abdeckung;
  if (!a || !a.zeichen) return null;
  const voll = a.lueckenlos && a.ausgewertet === a.abschnitte;
  return `Qwen: ${a.zeichen.toLocaleString('de-DE')} Zeichen in ${a.abschnitte} Abschnitt${a.abschnitte === 1 ? '' : 'en'} – `
    + (voll ? 'vollständig ausgewertet' : `nur ${a.ausgewertet} von ${a.abschnitte} Abschnitten ausgewertet (unvollständig)`);
}

/** Widerspruechliche Zuordnung (Praxisfall 23.10.2026): Hinweistext. Die Zuordnung selbst bleibt unveraendert, bis der Benutzer korrigiert. */
export function konfliktText(k) {
  if (!k) return '';
  return `${k.text || 'Zuordnung widersprüchlich'} (zugeordnet: ${k.klient_name || k.klient_id}).`;
}
